import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import usePageTitle from '../hooks/usePageTitle';
import ConfirmationModal from '../components/ConfirmationModal';
import api from '../services/api';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import './AdminDashboard.css';
import './attendance-modal-styles.css';

const COLORS = ['#00ff88', '#00cc6a', '#00aa55', '#008844', '#006633'];

const AdminDashboard = () => {
  usePageTitle('Admin Dashboard');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allMedia, setAllMedia] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    points: 10,
    poster: null,
  });
  const [createEventError, setCreateEventError] = useState('');
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamMemberModal, setShowTeamMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [teamFilterType, setTeamFilterType] = useState('cars');
  const [allEvents, setAllEvents] = useState([]);
  const [selectedEventForAttendance, setSelectedEventForAttendance] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPerPage] = useState(10);
  const [showGivePointsModal, setShowGivePointsModal] = useState(false);
  const [selectedUserForPoints, setSelectedUserForPoints] = useState(null);
  const [pointsForm, setPointsForm] = useState({
    points: 0,
    description: '',
  });
  const [givingPoints, setGivingPoints] = useState(false);
  const [teamMemberFormData, setTeamMemberFormData] = useState({
    name: '',
    name_arabic: '',
    photo: null,
    photo_arabic: null,
    team_type: 'cars',
    order: 0,
  });
  const [allDiscountPlaces, setAllDiscountPlaces] = useState([]);
  const [showDiscountPlaceModal, setShowDiscountPlaceModal] = useState(false);
  const [editingDiscountPlace, setEditingDiscountPlace] = useState(null);
  const [creatingDiscountPlace, setCreatingDiscountPlace] = useState(false);
  const [discountPlaceFormData, setDiscountPlaceFormData] = useState({
    place_name: '',
    discount_percentage: '',
    promo_code_suffix: '',
    description: '',
    user_id: '',
    logo: null,
  });
  const [discountPlaceError, setDiscountPlaceError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });
  const { isFounder } = useAuth();
  const { success, error: showError } = useToast();

  useEffect(() => {
    if (isFounder) {
      fetchAllData();
    }
  }, [isFounder]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllData = async () => {
    setLoading(true);
    try {
      console.log('Fetching all data...');
      const [usersRes, mediaRes, statsRes, teamRes, eventsRes, placesRes] = await Promise.all([
        api.get('/users').catch(err => {
          console.error('Error fetching users:', err);
          return { data: [] };
        }),
        api.get('/media/all').catch(err => {
          console.error('Error fetching media:', err);
          return { data: [] };
        }),
        api.get('/users/statistics').catch(err => {
          console.error('Error fetching statistics:', err);
          return { data: {} };
        }),
        api.get('/team-members').catch(err => {
          console.error('Error fetching team members:', err);
          return { data: [] };
        }),
        api.get('/events').catch(err => {
          console.error('Error fetching events:', err);
          return { data: [] };
        }),
        api.get('/discount-places').catch(err => {
          console.error('Error fetching discount places:', err);
          return { data: [] };
        }),
      ]);
      
      console.log('Raw API responses:');
      console.log('Users response:', usersRes);
      console.log('Media response:', mediaRes);
      console.log('Statistics response:', statsRes);
      console.log('Team response:', teamRes);
      console.log('Events response:', eventsRes);
      
      // Extract data from axios response structure
      // axios wraps the response in a 'data' property
      const usersData = Array.isArray(usersRes?.data) 
        ? usersRes.data 
        : (usersRes?.data?.data && Array.isArray(usersRes.data.data) 
          ? usersRes.data.data 
          : []);
      
      const mediaData = Array.isArray(mediaRes?.data) 
        ? mediaRes.data 
        : (mediaRes?.data?.data && Array.isArray(mediaRes.data.data) 
          ? mediaRes.data.data 
          : []);
      
      const statsData = statsRes?.data && typeof statsRes.data === 'object' 
        ? statsRes.data 
        : (statsRes?.data?.data && typeof statsRes.data.data === 'object'
          ? statsRes.data.data
          : {});
      
      const teamData = Array.isArray(teamRes?.data) 
        ? teamRes.data 
        : (teamRes?.data?.data && Array.isArray(teamRes.data.data) 
          ? teamRes.data.data 
          : []);
      
      const eventsData = Array.isArray(eventsRes?.data) 
        ? eventsRes.data 
        : (eventsRes?.data?.data && Array.isArray(eventsRes.data.data) 
          ? eventsRes.data.data 
          : []);

      const placesData = Array.isArray(placesRes?.data) 
        ? placesRes.data 
        : (placesRes?.data?.data && Array.isArray(placesRes.data.data) 
          ? placesRes.data.data 
          : []);
      
      console.log('Processed data counts - Users:', usersData.length, 'Media:', mediaData.length, 'Events:', eventsData.length, 'Places:', placesData.length);
      
      // Set state with processed data
      setAllUsers(usersData);
      setPendingUsers(usersData.filter(u => u && !u.verified));
      setAllMedia(mediaData);
      setStatistics(statsData);
      setTeamMembers(teamData);
      setAllEvents(eventsData);
      setAllDiscountPlaces(placesData);
      
      console.log('State updated successfully');
      
      // Show success message if data was loaded
      if (usersData.length > 0 || mediaData.length > 0 || eventsData.length > 0) {
        console.log('✅ Data loaded successfully');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      showError(error.response?.data?.message || 'Error loading data. Please try refreshing.');
      // Don't reset data on error - keep existing data if available
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (userId) => {
    try {
      await api.post(`/users/${userId}/verify`);
      success('User verified successfully');
      fetchAllData();
    } catch (error) {
      showError(error.response?.data?.message || 'Error verifying user');
    }
  };

  const handleApproveMedia = async (mediaId, approved) => {
    try {
      const response = await api.post(`/media/${mediaId}/approve`, { approved });
      success(approved ? 'Media approved successfully' : 'Media rejected');
      
      // Update the media item in the state immediately
      setAllMedia(prevMedia => 
        prevMedia.map(item => 
          item.id === mediaId 
            ? { ...item, approved: approved, rejected: !approved }
            : item
        )
      );
      
      // Also refresh data to ensure consistency
      fetchAllData();
    } catch (error) {
      showError(error.response?.data?.message || 'Error approving media');
    }
  };

  const handleDeleteUser = (userId) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/users/${userId}`);
          success('User deleted successfully');
          fetchAllData();
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting user');
        }
      }
    });
  };

  const handleDeleteMedia = (mediaId) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Media',
      message: 'Are you sure you want to delete this media? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/media/${mediaId}`);
          success('Media deleted successfully');
          fetchAllData();
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting media');
        }
      }
    });
  };

  const handleOpenTeamMemberModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setTeamMemberFormData({
        name: member.name,
        name_arabic: member.name_arabic,
        photo: null,
        photo_arabic: null,
        team_type: member.team_type,
        order: member.order,
      });
    } else {
      setEditingMember(null);
      setTeamMemberFormData({
        name: '',
        name_arabic: '',
        photo: null,
        photo_arabic: null,
        team_type: 'cars',
        order: 0,
      });
    }
    setShowTeamMemberModal(true);
  };

  const handleTeamMemberSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', teamMemberFormData.name);
      formData.append('name_arabic', teamMemberFormData.name_arabic);
      formData.append('team_type', teamMemberFormData.team_type);
      formData.append('order', teamMemberFormData.order);

      if (teamMemberFormData.photo) {
        formData.append('photo', teamMemberFormData.photo);
      }
      if (teamMemberFormData.photo_arabic) {
        formData.append('photo_arabic', teamMemberFormData.photo_arabic);
      }

      if (editingMember) {
        await api.put(`/team-members/${editingMember.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Team member updated successfully');
      } else {
        if (!teamMemberFormData.photo || !teamMemberFormData.photo_arabic) {
          showError('Please upload both photos');
          return;
        }
        await api.post('/team-members', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Team member added successfully');
      }

      setShowTeamMemberModal(false);
      fetchAllData();
    } catch (error) {
      showError(error.response?.data?.message || 'Error saving team member');
    }
  };

  const handleOpenAttendanceModal = (event) => {
    setSelectedEventForAttendance(event);
    const initialAttendance = {};
    event.registrations
      ?.filter(r => r.status === 'coming')
      .forEach(reg => {
        initialAttendance[reg.user_id] = reg.attended || false;
      });
    setAttendanceData(initialAttendance);
    setShowAttendanceModal(true);
  };

  const handleToggleAttendance = (userId) => {
    setAttendanceData(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleSaveAttendance = async () => {
    if (!selectedEventForAttendance) return;
    
    setSavingAttendance(true);
    try {
      // Get all registered users for this event
      const allRegisteredUsers = selectedEventForAttendance.registrations
        ?.filter(r => r.status === 'coming') || [];
      
      // Build attendance array with all registered users
      // This ensures we send attended: false for users who were unchecked
      const attendanceArray = allRegisteredUsers.map(reg => {
        const userId = reg.user_id;
        // If user is in attendanceData, use that value, otherwise use their current attended status
        const attended = attendanceData.hasOwnProperty(userId) 
          ? Boolean(attendanceData[userId]) 
          : (reg.attended || false);
        
        return {
          user_id: parseInt(userId),
          attended: attended
        };
      });

      // Log for debugging
      console.log('Saving attendance:', {
        eventId: selectedEventForAttendance.id,
        attendanceData: attendanceData,
        attendanceArray: attendanceArray,
        usersWithAttendedFalse: attendanceArray.filter(a => !a.attended)
      });

      const response = await api.post(
        `/events/${selectedEventForAttendance.id}/mark-attendance`,
        { attendance: attendanceArray }
      );

      console.log('Attendance response:', response.data);

      const pointsAdded = response.data.points_added || 0;
      const pointsRemoved = response.data.points_removed || 0;
      
      let message = '';
      if (pointsAdded > 0 && pointsRemoved > 0) {
        message = `Attendance saved! Points added to ${pointsAdded} users and removed from ${pointsRemoved} users.`;
      } else if (pointsAdded > 0) {
        message = `Attendance saved! Points added to ${pointsAdded} users.`;
      } else if (pointsRemoved > 0) {
        message = `Attendance saved! Points removed from ${pointsRemoved} users.`;
      } else {
        message = 'Attendance saved!';
      }
      
      success(message);
      setShowAttendanceModal(false);
      setSelectedEventForAttendance(null);
      setAttendanceData({});
      fetchAllData();
    } catch (error) {
      console.error('Error saving attendance:', error);
      showError(error.response?.data?.message || 'Error saving attendance');
    } finally {
      setSavingAttendance(false);
    }
  };

  const handleOpenGivePointsModal = (user) => {
    setSelectedUserForPoints(user);
    setPointsForm({
      points: 0,
      description: '',
    });
    setShowGivePointsModal(true);
  };

  const handleGivePoints = async (e) => {
    e.preventDefault();
    if (!selectedUserForPoints) return;

    setGivingPoints(true);
    try {
      const response = await api.post('/points/give', {
        user_id: selectedUserForPoints.id,
        points: parseInt(pointsForm.points),
        description: pointsForm.description || `Points given by admin`,
      });

      success(`Points added successfully! User now has ${response.data.points} points.`);
      setShowGivePointsModal(false);
      setSelectedUserForPoints(null);
      setPointsForm({
        points: 0,
        description: '',
      });
      fetchAllData();
    } catch (error) {
      showError(error.response?.data?.message || 'Error giving points');
    } finally {
      setGivingPoints(false);
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Team Member',
      message: 'Are you sure you want to delete this team member?',
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/team-members/${memberId}`);
          success('Team member deleted successfully');
          fetchAllData();
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting team member');
        }
        setConfirmationModal({ ...confirmationModal, isOpen: false });
      },
    });
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreateEventError('');
    setCreatingEvent(true);

    try {
      const data = new FormData();
      data.append('title', eventFormData.title);
      data.append('description', eventFormData.description);
      data.append('event_date', eventFormData.event_date);
      data.append('event_time', eventFormData.event_time);
      data.append('points', eventFormData.points || 10);
      if (eventFormData.poster) {
        data.append('poster', eventFormData.poster);
      }

      await api.post('/events', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      success('Event created successfully!');
      setShowCreateEventModal(false);
      setEventFormData({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        points: 10,
        poster: null,
      });
      fetchAllData();
    } catch (err) {
      setCreateEventError(err.response?.data?.message || 'Error creating event');
      console.error('Error creating event:', err);
    } finally {
      setCreatingEvent(false);
    }
  };

  const handleEventFormChange = (e) => {
    if (e.target.type === 'file') {
      setEventFormData({ ...eventFormData, poster: e.target.files[0] });
    } else {
      setEventFormData({ ...eventFormData, [e.target.name]: e.target.value });
    }
  };

  // Compress image function
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDiscountPlaceFormChange = (e) => {
    if (e.target.type === 'file') {
      setDiscountPlaceFormData({ ...discountPlaceFormData, logo: e.target.files[0] });
    } else {
      setDiscountPlaceFormData({ ...discountPlaceFormData, [e.target.name]: e.target.value });
    }
  };

  const handleCreateDiscountPlace = async (e) => {
    e.preventDefault();
    setDiscountPlaceError('');
    setCreatingDiscountPlace(true);

    try {
      const data = new FormData();
      data.append('place_name', discountPlaceFormData.place_name);
      data.append('discount_percentage', discountPlaceFormData.discount_percentage);
      data.append('promo_code_suffix', discountPlaceFormData.promo_code_suffix ? discountPlaceFormData.promo_code_suffix.toUpperCase().replace(/[^A-Z0-9-]/g, '') : '');
      data.append('description', discountPlaceFormData.description || '');
      
      if (isFounder && discountPlaceFormData.user_id) {
        data.append('user_id', discountPlaceFormData.user_id);
      }

      // Compress logo if larger than 2MB
      let logoFile = discountPlaceFormData.logo;
      if (logoFile && logoFile.size > 2 * 1024 * 1024) {
        console.log('Compressing logo...', logoFile.size);
        logoFile = await compressImage(logoFile);
      }

      if (logoFile) {
        data.append('logo', logoFile);
      }

      if (editingDiscountPlace) {
        data.append('_method', 'PUT');
        await api.post(`/discount-places/${editingDiscountPlace.id}`, data);
        success('Discount place updated successfully');
      } else {
        await api.post('/discount-places', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Discount place created successfully');
      }

      setShowDiscountPlaceModal(false);
      setEditingDiscountPlace(null);
      setDiscountPlaceFormData({ 
        place_name: '', 
        discount_percentage: '', 
        promo_code_suffix: '', 
        description: '', 
        user_id: '',
        logo: null 
      });
      fetchAllData();
    } catch (error) {
      setDiscountPlaceError(error.response?.data?.message || 'Error saving discount place');
    } finally {
      setCreatingDiscountPlace(false);
    }
  };

  const handleEditDiscountPlace = (place) => {
    setEditingDiscountPlace(place);
    // Extract suffix from promo_code (remove "ALIENS-" prefix if exists)
    let promoCodeSuffix = '';
    if (place.promo_code) {
      promoCodeSuffix = place.promo_code.replace(/^ALIENS-?/i, '');
    }

    setDiscountPlaceFormData({
      place_name: place.name || place.place_name,
      discount_percentage: place.discount_percentage,
      promo_code_suffix: promoCodeSuffix,
      description: place.description || '',
      user_id: place.user_id || '',
      logo: null,
    });
    setShowDiscountPlaceModal(true);
  };

  const handleDeleteDiscountPlace = async (id) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this discount place?',
      onConfirm: async () => {
        try {
          await api.delete(`/discount-places/${id}`);
          success('Discount place deleted successfully');
          fetchAllData();
        } catch (error) {
          showError('Error deleting discount place');
        }
      },
      type: 'danger'
    });
  };

  const filterDiscountPlaces = () => {
    if (!searchTerm) return allDiscountPlaces;
    const lowerSearch = searchTerm.toLowerCase();
    return allDiscountPlaces.filter(place =>
      place.place_name?.toLowerCase().includes(lowerSearch) ||
      place.promo_code?.toLowerCase().includes(lowerSearch)
    );
  };

  if (!isFounder) {
    return <div className="unauthorized">You are not authorized to access this page</div>;
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading dashboard...</div>
      </div>
    );
  }

  // Debug: Log current state
  console.log('Current state:', {
    allUsers: allUsers.length,
    allMedia: allMedia.length,
    allEvents: allEvents.length,
    teamMembers: teamMembers.length,
    statistics: statistics,
    pendingUsers: pendingUsers.length,
  });

  // Check if statistics object has meaningful data
  const hasStatistics = statistics && Object.keys(statistics).length > 0;

  // Prepare chart data
  const roleDistribution = (hasStatistics && statistics.users_by_role && Object.keys(statistics.users_by_role).length > 0)
    ? Object.entries(statistics.users_by_role).map(([role, count]) => ({
        name: role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' '),
        value: count,
      }))
    : allUsers.length > 0
    ? Object.entries(
        allUsers.reduce((acc, user) => {
          if (user && user.role) {
            acc[user.role] = (acc[user.role] || 0) + 1;
          }
          return acc;
        }, {})
      ).map(([role, count]) => ({
        name: role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' '),
        value: count,
      }))
    : [];

  const mediaStatusData = (hasStatistics && (statistics.approved_media !== undefined || statistics.pending_media !== undefined))
    ? [
        { name: 'Approved', value: statistics.approved_media || 0, color: '#00ff88' },
        { name: 'Pending', value: statistics.pending_media || 0, color: '#ffaa00' },
      ]
    : allMedia.length > 0
    ? [
        { name: 'Approved', value: allMedia.filter(m => m && m.approved).length, color: '#00ff88' },
        { name: 'Pending', value: allMedia.filter(m => m && !m.approved && !m.rejected).length, color: '#ffaa00' },
      ]
    : [];

  const userStatusData = (hasStatistics && (statistics.verified_users !== undefined || statistics.pending_users !== undefined))
    ? [
        { name: 'Verified', value: statistics.verified_users || 0, color: '#00ff88' },
        { name: 'Pending', value: statistics.pending_users || 0, color: '#ffaa00' },
      ]
    : allUsers.length > 0
    ? [
        { name: 'Verified', value: allUsers.filter(u => u && u.verified).length, color: '#00ff88' },
        { name: 'Pending', value: allUsers.filter(u => u && !u.verified).length, color: '#ffaa00' },
      ]
    : [];

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>👽 Admin Dashboard</h1>
        <div className="header-actions">
          <button className="btn-refresh" onClick={fetchAllData}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          👥 Users ({pendingUsers.length} pending)
        </button>
        <button
          className={activeTab === 'media' ? 'active' : ''}
          onClick={() => setActiveTab('media')}
        >
          🎬 Media ({allMedia.filter(m => !m.approved && !m.rejected).length} pending)
        </button>
        <button
          className={activeTab === 'events' ? 'active' : ''}
          onClick={() => setActiveTab('events')}
        >
          📅 Events
        </button>
        <button
          className={activeTab === 'team' ? 'active' : ''}
          onClick={() => setActiveTab('team')}
        >
          👽 Team
        </button>
        <button
          className={activeTab === 'points' ? 'active' : ''}
          onClick={() => setActiveTab('points')}
        >
          ⭐ Points Management
        </button>
        <button
          className={activeTab === 'discount-places' ? 'active' : ''}
          onClick={() => setActiveTab('discount-places')}
        >
          🏪 Discount Places
        </button>
      </div>

      <div className="admin-quick-links">
        <Link to="/admin/products" className="admin-link-card">
          <div className="link-icon">🛍️</div>
          <div className="link-content">
            <h3>Manage Products</h3>
            <p>Add, edit, or remove products from the shop</p>
          </div>
          <div className="link-arrow">→</div>
        </Link>
        <Link to="/admin/giveaways" className="admin-link-card">
          <div className="link-icon">🎁</div>
          <div className="link-content">
            <h3>Manage Giveaways</h3>
            <p>Control giveaways and their visibility</p>
          </div>
          <div className="link-arrow">→</div>
        </Link>
        <Link to="/admin/photographers" className="admin-link-card">
          <div className="link-icon">📸</div>
          <div className="link-content">
            <h3>Manage Photographers</h3>
            <p>Add photographers and their packages</p>
          </div>
          <div className="link-arrow">→</div>
        </Link>
      </div>

      {activeTab === 'overview' && (
        <div className="overview-section">
          {/* Statistics Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{hasStatistics && statistics.total_users !== undefined ? statistics.total_users : allUsers.length}</h3>
                <p>Total Users</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{hasStatistics && statistics.verified_users !== undefined ? statistics.verified_users : allUsers.filter(u => u && u.verified).length}</h3>
                <p>Verified Users</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <h3>{hasStatistics && statistics.pending_users !== undefined ? statistics.pending_users : pendingUsers.length}</h3>
                <p>Pending Users</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <h3>{hasStatistics && statistics.total_events !== undefined ? statistics.total_events : allEvents.length}</h3>
                <p>Total Events</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎬</div>
              <div className="stat-content">
                <h3>{hasStatistics && statistics.total_media !== undefined ? statistics.total_media : allMedia.length}</h3>
                <p>Total Media</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏪</div>
              <div className="stat-content">
                <h3>{hasStatistics && statistics.total_discount_places !== undefined ? statistics.total_discount_places : 0}</h3>
                <p>Discount Places</p>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="charts-grid">
            <div className="chart-card">
              <h3>Users by Role</h3>
              {roleDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={roleDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {roleDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="no-data">No data available</p>
              )}
            </div>

            <div className="chart-card">
              <h3>Media Status</h3>
              {mediaStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={mediaStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {mediaStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="no-data">No data available</p>
              )}
            </div>

            <div className="chart-card">
              <h3>User Status</h3>
              {userStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={userStatusData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="#00ff88" />
                    <YAxis stroke="#00ff88" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(10, 14, 39, 0.95)',
                        border: '1px solid #00ff88',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="value" fill="#00ff88" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="no-data">No data available</p>
              )}
            </div>

            <div className="chart-card">
              <h3>Recent Users</h3>
              <div className="recent-users-list">
                {(statistics?.recent_users && statistics.recent_users.length > 0) ? (
                  statistics.recent_users.map((user) => (
                    <div key={user.id} className="recent-user-item">
                      <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                      <div className="user-details">
                        <p className="user-name">{user.name}</p>
                        <p className="user-email">{user.email}</p>
                        <p className="user-role">{user.role}</p>
                      </div>
                      <div className="user-date">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))
                ) : allUsers.length > 0 ? (
                  allUsers.slice(0, 5).map((user) => (
                    <div key={user.id} className="recent-user-item">
                      <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                      <div className="user-details">
                        <p className="user-name">{user.name}</p>
                        <p className="user-email">{user.email}</p>
                        <p className="user-role">{user.role}</p>
                      </div>
                      <div className="user-date">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">No users found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="pending-users">
          <div className="section-header">
            <h2>Users Management</h2>
            <div className="section-stats">
              <span>Total: {allUsers.length}</span>
              <span>Pending: {pendingUsers.length}</span>
            </div>
          </div>
          {allUsers.length === 0 ? (
            <p className="no-data">No users found</p>
          ) : (
            <div className="users-list">
              {allUsers.map((user) => (
                <div 
                  key={user.id} 
                  className="user-card-simple"
                  onClick={() => {
                    setSelectedUser(user);
                    setShowUserModal(true);
                  }}
                >
                  <div className="user-info-simple">
                    {user.profile_image ? (
                      <img
                        src={user.profile_image_url 
                          ? `${user.profile_image_url}?t=${Date.now()}` 
                          : (user.profile_image?.startsWith('http') 
                            ? `${user.profile_image}?t=${Date.now()}` 
                            : `https://aliensteam.com/storage/app/public/${user.profile_image}?t=${Date.now()}`)}
                        alt="Profile"
                        className="user-avatar-simple"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const avatar = e.target.parentElement.querySelector('.user-avatar-fallback');
                          if (avatar) avatar.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="user-avatar-fallback"
                      style={{ display: user.profile_image ? 'none' : 'flex' }}
                    >
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-details-simple">
                      <h3>{user.name}</h3>
                      <p>📧 {user.email}</p>
                      <p>📱 {user.phone}</p>
                      <p>👤 Role: <span className="role-badge">{user.role}</span></p>
                      <p>Status: {user.verified ? (
                        <span className="status-verified">✅ Verified</span>
                      ) : (
                        <span className="status-pending">⏳ Pending</span>
                      )}</p>
                    </div>
                  </div>
                  <div className="user-actions-simple" onClick={(e) => e.stopPropagation()}>
                    {!user.verified && (
                      <button
                        className="btn-success"
                        onClick={() => handleVerify(user.id)}
                      >
                        ✅ Approve
                      </button>
                    )}
                    <button
                      className="btn-danger"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'media' && (
        <div className="media-approval">
          <div className="section-header">
            <h2>Media Management</h2>
            <div className="section-stats">
              <span>Total: {allMedia.length}</span>
              <span>Pending: {allMedia.filter(m => !m.approved && !m.rejected).length}</span>
              <span>Rejected: {allMedia.filter(m => m.rejected).length}</span>
            </div>
          </div>
          <div className="media-list">
            {allMedia.map((item) => (
              <div key={item.id} className="media-approval-item">
                <div className="media-preview-wrapper">
                  {item.file_type === 'image' ? (
                    <img
                      src={(() => {
                        if (item.file_path.startsWith('http')) {
                          return item.file_path;
                        }
                        let path = item.file_path;
                        if (path.startsWith('storage/')) {
                          path = path.replace('storage/', '');
                        }
                        return `https://aliensteam.com/app-media/${path}?t=${Date.now()}`;
                      })()}
                      alt="Media"
                      className="media-preview"
                      onError={(e) => {
                        console.error('Error loading media image:', item.file_path, 'for item:', item.id);
                        const defaultImage = 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=200&h=150&fit=crop&q=80';
                        if (e.target.src !== defaultImage) {
                          e.target.src = defaultImage;
                        }
                      }}
                    />
                  ) : (
                    <video
                      src={(() => {
                        if (item.file_path.startsWith('http')) {
                          return item.file_path;
                        }
                        let path = item.file_path;
                        if (path.startsWith('storage/')) {
                          path = path.replace('storage/', '');
                        }
                        return `https://aliensteam.com/app-media/${path}?t=${Date.now()}`;
                      })()}
                      controls
                      className="media-preview"
                      poster="https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=200&h=150&fit=crop&q=80"
                      onError={(e) => {
                        console.error('Error loading media video:', item.file_path, 'for item:', item.id);
                      }}
                    />
                  )}
                  <div className={`media-status-badge ${item.approved ? 'approved' : item.rejected ? 'rejected' : 'pending'}`}>
                    {item.approved ? '✅ Approved' : item.rejected ? '❌ Rejected' : '⏳ Pending'}
                  </div>
                </div>
                <div className="media-info">
                  <p><strong>👤 User:</strong> {item.user?.name || 'Unknown'}</p>
                  {item.event && <p><strong>📅 Event:</strong> {item.event.title}</p>}
                  <p><strong>📁 Type:</strong> {item.file_type}</p>
                  <p><strong>📆 Uploaded:</strong> {new Date(item.created_at).toLocaleDateString()}</p>
                </div>
                <div className="media-actions">
                  {item.rejected ? (
                    <div className="media-rejected-message">
                      <span className="rejected-text">❌ Rejected</span>
                    </div>
                  ) : (
                    <>
                      {!item.approved && (
                        <button
                          className="btn-success"
                          onClick={() => handleApproveMedia(item.id, true)}
                        >
                          ✅ Approve
                        </button>
                      )}
                      {!item.approved && (
                        <button
                          className="btn-warning"
                          onClick={() => handleApproveMedia(item.id, false)}
                        >
                          ❌ Reject
                        </button>
                      )}
                      <button
                        className="btn-danger"
                        onClick={() => handleDeleteMedia(item.id)}
                      >
                        🗑️ Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'events' && (
        <div className="events-management">
          <div className="section-header">
            <h2>Events Management</h2>
            <button className="btn-primary" onClick={() => setShowCreateEventModal(true)}>
              ➕ Add New Event
            </button>
          </div>
          {allEvents.length === 0 ? (
            <p className="info-text">No events found.</p>
          ) : (
            <>
              <div className="events-table-wrapper">
                <table className="events-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Points</th>
                      <th>Coming</th>
                      <th>Attended</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allEvents
                      .slice((eventsPage - 1) * eventsPerPage, eventsPage * eventsPerPage)
                      .map((event) => {
                        const comingCount = event.registrations?.filter(r => r.status === 'coming').length || 0;
                        const attendedCount = event.registrations?.filter(r => r.status === 'coming' && r.attended).length || 0;
                        const eventDate = new Date(event.event_date);
                        const isPastEvent = eventDate < new Date();
                        const isToday = eventDate.toDateString() === new Date().toDateString();
                        
                        return (
                          <tr key={event.id}>
                            <td>
                              <div className="event-title-cell">
                                <strong>{event.title}</strong>
                                {event.description && (
                                  <span className="event-description-small" title={event.description}>
                                    {event.description.length > 50 ? event.description.substring(0, 50) + '...' : event.description}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>{eventDate.toLocaleDateString()}</td>
                            <td>{event.event_time}</td>
                            <td>
                              <span className="points-badge">⭐ {event.points || 10}</span>
                            </td>
                            <td>
                              <span className="coming-badge">👥 {comingCount}</span>
                            </td>
                            <td>
                              {isPastEvent ? (
                                <span className="attended-badge">✅ {attendedCount}</span>
                              ) : (
                                <span className="not-applicable">-</span>
                              )}
                            </td>
                            <td>
                              {isPastEvent ? (
                                <span className="status-badge past">Past</span>
                              ) : isToday ? (
                                <span className="status-badge today">Today</span>
                              ) : (
                                <span className="status-badge upcoming">Upcoming</span>
                              )}
                            </td>
                            <td>
                              {isPastEvent && comingCount > 0 && (
                                <button
                                  className="btn-attendance"
                                  onClick={() => handleOpenAttendanceModal(event)}
                                  title="Mark Attendance"
                                >
                                  📝 Mark Attendance
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              {allEvents.length > eventsPerPage && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => setEventsPage(prev => Math.max(1, prev - 1))}
                    disabled={eventsPage === 1}
                  >
                    ← Previous
                  </button>
                  <span className="pagination-info">
                    Page {eventsPage} of {Math.ceil(allEvents.length / eventsPerPage)}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setEventsPage(prev => Math.min(Math.ceil(allEvents.length / eventsPerPage), prev + 1))}
                    disabled={eventsPage >= Math.ceil(allEvents.length / eventsPerPage)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'discount-places' && (
        <div className="discount-places-section">
          <div className="section-header">
            <div className="header-main">
              <h2>🏪 Discount Places</h2>
              <button className="btn-primary" onClick={() => {
                setEditingDiscountPlace(null);
                setDiscountPlaceFormData({ place_name: '', discount_percentage: '', promo_code_suffix: '', description: '', user_id: '', logo: null });
                setShowDiscountPlaceModal(true);
              }}>
                ➕ Add New Place
              </button>
            </div>
            <div className="header-search">
              <input
                type="text"
                placeholder="Search by name or promo code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="places-grid">
            {filterDiscountPlaces().map((place) => (
              <div key={place.id} className="place-card">
                <div className="place-image">
                  <img
                    src={place.logo_url ? `${place.logo_url}${place.logo_url.includes('?') ? '&' : '?'}t=${Date.now()}` : `https://aliensteam.com/app-media/${place.logo}?t=${Date.now()}`}
                    alt={place.place_name}
                    onError={(e) => { 
                      const currentSrc = e.target.src;
                      const retryCount = parseInt(e.target.getAttribute('data-retry') || '0');
                      console.log(`Admin Logo failed (retry ${retryCount}):`, currentSrc);
                      
                      if (retryCount < 2) {
                        e.target.setAttribute('data-retry', retryCount + 1);
                        if (currentSrc.includes('/storage/app/public/')) {
                          e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                        } else if (currentSrc.includes('/storage/')) {
                          e.target.src = currentSrc.replace('/storage/', '/storage/app/public/');
                        } else if (!currentSrc.includes('unsplash')) {
                          e.target.src = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=200&fit=crop&q=80';
                        }
                      } else if (!currentSrc.includes('unsplash')) {
                        e.target.src = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=200&fit=crop&q=80'; 
                      }
                    }}
                  />
                  <div className="discount-tag">{place.discount_percentage}% OFF</div>
                </div>
                <div className="place-info">
                  <h3>{place.place_name}</h3>
                  <p className="promo-preview">Code: <strong>{place.promo_code}</strong></p>
                  <p className="place-desc">{place.description || 'No description available'}</p>
                  <p className="place-owner-info">Owner: {allUsers.find(u => u.id === place.user_id)?.name || 'N/A'}</p>
                  <div className="place-actions">
                    <button className="btn-edit" onClick={() => handleEditDiscountPlace(place)}>✏️ Edit</button>
                    <button className="btn-danger" onClick={() => handleDeleteDiscountPlace(place.id)}>🗑️ Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {filterDiscountPlaces().length === 0 && (
            <div className="empty-state">
              <p>No discount places found. Add your first one!</p>
            </div>
          )}
        </div>
      )}


      {/* Create Event Modal */}
      {showCreateEventModal && (
        <div className="modal-overlay" onClick={() => setShowCreateEventModal(false)}>
          <div className="modal-content create-event-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCreateEventModal(false)}>
              ✕
            </button>
            <div className="modal-header">
              <h2>✨ Create New Event</h2>
            </div>
            {createEventError && <div className="error-message">{createEventError}</div>}
            <form onSubmit={handleCreateEvent}>
              <div className="form-group">
                <label>📝 Event Title</label>
                <input
                  type="text"
                  name="title"
                  value={eventFormData.title}
                  onChange={handleEventFormChange}
                  placeholder="Enter event title"
                  required
                />
              </div>

              <div className="form-group">
                <label>📄 Description</label>
                <textarea
                  name="description"
                  value={eventFormData.description}
                  onChange={handleEventFormChange}
                  placeholder="Enter event description"
                  rows="5"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>📅 Event Date</label>
                  <input
                    type="date"
                    name="event_date"
                    value={eventFormData.event_date}
                    onChange={handleEventFormChange}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>🕐 Event Time</label>
                  <input
                    type="time"
                    name="event_time"
                    value={eventFormData.event_time}
                    onChange={handleEventFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>⭐ Points Reward</label>
                <input
                  type="number"
                  name="points"
                  value={eventFormData.points}
                  onChange={handleEventFormChange}
                  placeholder="Points to give for attending"
                  min="0"
                  required
                />
                <small className="form-help">Points users will receive when they register as "Coming"</small>
              </div>

              <div className="form-group">
                <label>🖼️ Event Poster (Optional)</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    name="poster"
                    accept="image/*"
                    onChange={handleEventFormChange}
                    className="file-input"
                    id="poster-upload-admin"
                  />
                  <label htmlFor="poster-upload-admin" className="file-upload-label">
                    {eventFormData.poster ? eventFormData.poster.name : 'Choose Image'}
                  </label>
                </div>
                {eventFormData.poster && (
                  <div className="preview-container">
                    <img
                      src={URL.createObjectURL(eventFormData.poster)}
                      alt="Poster preview"
                      className="poster-preview"
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateEventModal(false)}
                  disabled={creatingEvent}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={creatingEvent}
                >
                  {creatingEvent ? 'Creating...' : '✨ Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUserModal(false)}>
              ✕
            </button>
            <div className="modal-header">
              <h2>👤 User Details</h2>
            </div>
            
            <div className="user-modal-content">
              {/* User Profile Section */}
              <div className="user-modal-profile">
                {selectedUser.profile_image ? (
                  <img
                    src={selectedUser.profile_image_url 
                      ? `${selectedUser.profile_image_url}?t=${Date.now()}` 
                      : (selectedUser.profile_image?.startsWith('http') 
                        ? `${selectedUser.profile_image}?t=${Date.now()}` 
                        : `https://aliensteam.com/app-media/${selectedUser.profile_image}?t=${Date.now()}`)}
                    alt="Profile"
                    className="user-modal-profile-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const avatar = e.target.parentElement.querySelector('.user-modal-avatar');
                      if (avatar) avatar.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="user-modal-avatar"
                  style={{ display: selectedUser.profile_image ? 'none' : 'flex' }}
                >
                  {selectedUser.name.charAt(0).toUpperCase()}
                </div>
                <div className="user-modal-basic-info">
                  <h3>{selectedUser.name}</h3>
                  <p>📧 {selectedUser.email}</p>
                  <p>📱 {selectedUser.phone}</p>
                  <p>👤 Role: <span className="role-badge">{selectedUser.role}</span></p>
                  <p>Status: {selectedUser.verified ? (
                    <span className="status-verified">✅ Verified</span>
                  ) : (
                    <span className="status-pending">⏳ Pending</span>
                  )}</p>
                </div>
              </div>

              {/* ID Card Section */}
              {selectedUser.id_card_path && (
                <div className="modal-review-section">
                  <h4 className="modal-section-title">🆔 ID Card</h4>
                  <div className="modal-image-gallery">
                    <img
                      src={selectedUser.id_card_url 
                        ? `${selectedUser.id_card_url}?t=${Date.now()}` 
                        : (selectedUser.id_card_path?.startsWith('http') 
                          ? `${selectedUser.id_card_path}?t=${Date.now()}` 
                          : `https://aliensteam.com/app-media/${selectedUser.id_card_path}?t=${Date.now()}`)}
                      alt="ID Card"
                      className="modal-review-image"
                      onError={(e) => {
                        const currentSrc = e.target.src;
                        if (currentSrc.includes('/storage/app/public/')) {
                          e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                        } else {
                          e.target.src = 'https://via.placeholder.com/400x300/0a0e27/00ff88?text=ID+Card+Not+Found';
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Vehicle Section (for owners) */}
              {selectedUser.vehicle && (
                <div className="modal-review-section">
                  <h4 className="modal-section-title">🚗 Vehicle Information</h4>
                  <div className="modal-vehicle-info">
                    <p><strong>Vehicle Type:</strong> {selectedUser.vehicle.vehicle_type === 'car' ? 'Car' : 'Bike'}</p>
                    {selectedUser.vehicle.license_photo && (
                      <div className="modal-image-item">
                        <p className="modal-image-label">📄 Driving License:</p>
                        <img
                          src={selectedUser.vehicle?.license_photo_url 
                            ? `${selectedUser.vehicle.license_photo_url}?t=${Date.now()}` 
                            : (selectedUser.vehicle?.license_photo?.startsWith('http') 
                              ? `${selectedUser.vehicle.license_photo}?t=${Date.now()}` 
                              : `https://aliensteam.com/app-media/${selectedUser.vehicle.license_photo}?t=${Date.now()}`)}
                          alt="License"
                          className="modal-review-image"
                          onError={(e) => {
                            const currentSrc = e.target.src;
                            if (currentSrc.includes('/storage/app/public/')) {
                              e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                            } else {
                              e.target.src = 'https://via.placeholder.com/400x300/0a0e27/00ff88?text=License+Not+Found';
                            }
                          }}
                        />
                      </div>
                    )}
                    {selectedUser.vehicle.vehicle_photo && (
                      <div className="modal-image-item">
                        <p className="modal-image-label">🚗 Vehicle Photo:</p>
                        <img
                          src={selectedUser.vehicle?.vehicle_photo_url 
                            ? `${selectedUser.vehicle.vehicle_photo_url}?t=${Date.now()}` 
                            : (selectedUser.vehicle?.vehicle_photo?.startsWith('http') 
                              ? `${selectedUser.vehicle.vehicle_photo}?t=${Date.now()}` 
                              : `https://aliensteam.com/app-media/${selectedUser.vehicle.vehicle_photo}?t=${Date.now()}`)}
                          alt="Vehicle"
                          className="modal-review-image"
                          onError={(e) => {
                            const currentSrc = e.target.src;
                            if (currentSrc.includes('/storage/app/public/')) {
                              e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                            } else {
                              e.target.src = 'https://via.placeholder.com/400x300/0a0e27/00ff88?text=Vehicle+Not+Found';
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* User Uploaded Media Section */}
              {selectedUser.media && selectedUser.media.length > 0 && (
                <div className="modal-review-section">
                  <h4 className="modal-section-title">📸 Uploaded Media ({selectedUser.media.length})</h4>
                  <div className="modal-media-gallery">
                    {selectedUser.media.map((mediaItem) => (
                      <div key={mediaItem.id} className="modal-media-gallery-item">
                        {mediaItem.file_type === 'image' ? (
                          <img
                            src={(() => {
                              if (mediaItem.file_path.startsWith('http')) {
                                return mediaItem.file_path;
                              }
                              let path = mediaItem.file_path;
                              if (path.startsWith('storage/')) {
                                path = path.replace('storage/', '');
                              }
                              return `https://aliensteam.com/app-media/${path}?t=${Date.now()}`;
                            })()}
                            alt="User Media"
                            className="modal-gallery-image"
                            onError={(e) => {
                              const currentSrc = e.target.src;
                              if (currentSrc.includes('/storage/app/public/')) {
                                e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                              } else if (currentSrc.includes('/storage/') && !currentSrc.includes('/app/public/')) {
                                e.target.src = currentSrc.replace('/storage/', '/storage/app/public/');
                              } else {
                                e.target.src = 'https://via.placeholder.com/200x150/0a0e27/00ff88?text=Image+Not+Found';
                              }
                            }}
                          />
                        ) : (
                          <video
                            src={(() => {
                              if (mediaItem.file_path.startsWith('http')) {
                                return mediaItem.file_path;
                              }
                              let path = mediaItem.file_path;
                              if (path.startsWith('storage/')) {
                                path = path.replace('storage/', '');
                              }
                              return `https://aliensteam.com/storage/${path}?t=${Date.now()}`;
                            })()}
                            controls
                            className="modal-gallery-video"
                            onError={(e) => {
                              const currentSrc = e.target.src;
                              if (currentSrc.includes('/storage/app/public/')) {
                                e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                                return;
                              }
                              if (currentSrc.includes('/storage/') && !currentSrc.includes('/app/public/')) {
                                e.target.src = currentSrc.replace('/storage/', '/storage/app/public/');
                                return;
                              }
                              console.error('Error loading video:', mediaItem.file_path);
                            }}
                          />
                        )}
                        <div className={`modal-media-item-status ${mediaItem.approved ? 'approved' : 'pending'}`}>
                          {mediaItem.approved ? '✅ Approved' : '⏳ Pending Review'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Discount Place Section (if applicable) */}
              {selectedUser.discountPlace && (
                <div className="modal-review-section">
                  <h4 className="modal-section-title">🏪 Discount Place Information</h4>
                  <div className="modal-discount-place-info">
                    <p><strong>Name:</strong> {selectedUser.discountPlace.name || 'Not Available'}</p>
                    <p><strong>Description:</strong> {selectedUser.discountPlace.description || 'Not Available'}</p>
                    {selectedUser.discountPlace.image && (
                      <div className="modal-image-item">
                        <p className="modal-image-label">Place Image:</p>
                        <img
                          src={selectedUser.discountPlace?.logo_url 
                            ? `${selectedUser.discountPlace.logo_url}?t=${Date.now()}` 
                            : (selectedUser.discountPlace?.logo?.startsWith('http') 
                              ? `${selectedUser.discountPlace.logo}?t=${Date.now()}` 
                              : (selectedUser.discountPlace?.logo 
                                ? `https://aliensteam.com/storage/app/public/${selectedUser.discountPlace.logo}?t=${Date.now()}` 
                                : ''))}
                          alt="Discount Place"
                          className="modal-review-image"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x300/0a0e27/00ff88?text=Image+Not+Found';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="modal-user-actions">
                {!selectedUser.verified && (
                  <button
                    className="btn-success"
                    onClick={() => {
                      handleVerify(selectedUser.id);
                      setShowUserModal(false);
                    }}
                  >
                    ✅ Approve
                  </button>
                )}
                <button
                  className="btn-danger"
                  onClick={() => {
                    setShowUserModal(false);
                    handleDeleteUser(selectedUser.id);
                  }}
                >
                  🗑️ Delete
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setShowUserModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Management Tab */}
      {activeTab === 'team' && (
        <div className="team-management-section">
          <div className="section-header">
            <h2>👽 Team Management</h2>
            <button className="btn-primary" onClick={() => handleOpenTeamMemberModal()}>
              ➕ Add Team Member
            </button>
          </div>

          <div className="team-filters">
            <button
              className={teamFilterType === 'cars' ? 'active' : ''}
              onClick={() => setTeamFilterType('cars')}
            >
              🚗 Cars
            </button>
            <button
              className={teamFilterType === 'bikes' ? 'active' : ''}
              onClick={() => setTeamFilterType('bikes')}
            >
              🏍️ Bikes
            </button>
          </div>

          <div className="team-members-grid">
            {teamMembers
              .filter(m => m.team_type === teamFilterType)
              .sort((a, b) => a.order - b.order)
              .map((member) => (
                <div key={member.id} className="team-member-card">
                  <div className="team-member-photos">
                    <div className="team-member-photo">
                      <img
                        src={member.photo_url ? `${member.photo_url}?t=${Date.now()}` : `https://aliensteam.com/storage/${member.photo_path}?t=${Date.now()}`}
                        alt={member.name}
                        onError={(e) => {
                          const currentSrc = e.target.src;
                          if (currentSrc.includes('/storage/app/public/')) {
                            e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                          } else {
                            e.target.src = 'https://via.placeholder.com/200x200/0a0e27/ff00ff?text=Photo';
                          }
                        }}
                      />
                      <span className="photo-label">English</span>
                    </div>
                    <div className="team-member-photo">
                      <img
                        src={member.photo_arabic_url ? `${member.photo_arabic_url}?t=${Date.now()}` : `https://aliensteam.com/storage/${member.photo_arabic_path}?t=${Date.now()}`}
                        alt={member.name_arabic}
                        onError={(e) => {
                          const currentSrc = e.target.src;
                          if (currentSrc.includes('/storage/app/public/')) {
                            e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                          } else {
                            e.target.src = 'https://via.placeholder.com/200x200/0a0e27/ff00ff?text=Photo+Arabic';
                          }
                        }}
                      />
                      <span className="photo-label">Arabic</span>
                    </div>
                  </div>
                  <div className="team-member-info">
                    <h3>{member.name}</h3>
                    <p className="arabic-name">{member.name_arabic}</p>
                    <span className="team-type-badge">{member.team_type === 'cars' ? '🚗 Cars' : '🏍️ Bikes'}</span>
                  </div>
                  <div className="team-member-actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleOpenTeamMemberModal(member)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn-danger"
                      onClick={() => handleDeleteTeamMember(member.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
          </div>

          {teamMembers.filter(m => m.team_type === teamFilterType).length === 0 && (
            <div className="empty-state">
              <p>No team members found. Add your first team member!</p>
            </div>
          )}
        </div>
      )}

      {/* Points Management Tab */}
      {activeTab === 'points' && (
        <div className="points-management-section">
          <div className="section-header">
            <h2>⭐ Points Management</h2>
            <p className="section-description">Give points to users manually</p>
          </div>
          <div className="users-points-list">
            {allUsers.length === 0 ? (
              <p className="no-data">No users found</p>
            ) : (
              <table className="points-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Current Points</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-info-cell">
                          <span className="user-avatar-small">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                          <span>{user.name}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className="points-display">⭐ {user.points || 0}</span>
                      </td>
                      <td>
                        <span className="role-badge">{user.role}</span>
                      </td>
                      <td>
                        <button
                          className="btn-give-points"
                          onClick={() => handleOpenGivePointsModal(user)}
                        >
                          ➕ Give Points
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Give Points Modal */}
      {showGivePointsModal && selectedUserForPoints && (
        <div className="modal-overlay give-points-modal-overlay" onClick={() => setShowGivePointsModal(false)}>
          <div className="modal-content give-points-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close give-points-modal-close" onClick={() => setShowGivePointsModal(false)}>
              ✕
            </button>
            <div className="give-points-modal-header">
              <div className="give-points-modal-icon">⭐</div>
              <div className="give-points-modal-title-section">
                <h2 className="give-points-modal-title">Give Points</h2>
                <div className="give-points-modal-user-info">
                  <p className="give-points-modal-user-name">
                    Give points to: <strong>{selectedUserForPoints.name}</strong>
                  </p>
                  <p className="give-points-modal-current-points">
                    Current Points: <strong>⭐ {selectedUserForPoints.points || 0}</strong>
                  </p>
                </div>
              </div>
            </div>
            <div className="give-points-modal-body">
              <form onSubmit={handleGivePoints}>
                <div className="form-group">
                  <label>Points to Add</label>
                  <input
                    type="number"
                    min="1"
                    value={pointsForm.points}
                    onChange={(e) => setPointsForm({ ...pointsForm, points: e.target.value })}
                    placeholder="Enter points"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    value={pointsForm.description}
                    onChange={(e) => setPointsForm({ ...pointsForm, description: e.target.value })}
                    placeholder="Reason for giving points"
                    rows="3"
                  />
                </div>
                <div className="give-points-modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowGivePointsModal(false);
                      setSelectedUserForPoints(null);
                      setPointsForm({ points: 0, description: '' });
                    }}
                    disabled={givingPoints}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={givingPoints}
                  >
                    {givingPoints ? (
                      <>
                        <span className="loading-spinner-small"></span>
                        Adding...
                      </>
                    ) : (
                      <>
                        ➕ Give Points
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Team Member Modal */}
      {showTeamMemberModal && (
        <div className="modal-overlay" onClick={() => setShowTeamMemberModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowTeamMemberModal(false)}>
              ✕
            </button>
            <div className="modal-header">
              <h2>{editingMember ? '✏️ Edit Team Member' : '➕ Add Team Member'}</h2>
            </div>
            <form onSubmit={handleTeamMemberSubmit}>
              <div className="form-group">
                <label>👤 Name (English)</label>
                <input
                  type="text"
                  value={teamMemberFormData.name}
                  onChange={(e) => setTeamMemberFormData({ ...teamMemberFormData, name: e.target.value })}
                  placeholder="Enter name in English"
                  required
                />
              </div>

              <div className="form-group">
                <label>👤 Name (Arabic)</label>
                <input
                  type="text"
                  value={teamMemberFormData.name_arabic}
                  onChange={(e) => setTeamMemberFormData({ ...teamMemberFormData, name_arabic: e.target.value })}
                  placeholder="Enter name in Arabic"
                  required
                />
              </div>

              <div className="form-group">
                <label>📸 Photo (English)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setTeamMemberFormData({ ...teamMemberFormData, photo: e.target.files[0] })}
                  required={!editingMember}
                />
                {editingMember && !teamMemberFormData.photo && (
                  <p className="form-hint">Leave empty to keep current photo</p>
                )}
              </div>

              <div className="form-group">
                <label>📸 Photo (Arabic)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setTeamMemberFormData({ ...teamMemberFormData, photo_arabic: e.target.files[0] })}
                  required={!editingMember}
                />
                {editingMember && !teamMemberFormData.photo_arabic && (
                  <p className="form-hint">Leave empty to keep current photo</p>
                )}
              </div>

              <div className="form-group">
                <label>🏎️ Team Type</label>
                <select
                  value={teamMemberFormData.team_type}
                  onChange={(e) => setTeamMemberFormData({ ...teamMemberFormData, team_type: e.target.value })}
                  required
                >
                  <option value="cars">🚗 Cars</option>
                  <option value="bikes">🏍️ Bikes</option>
                </select>
              </div>

              <div className="form-group">
                <label>🔢 Order</label>
                <input
                  type="number"
                  min="0"
                  value={teamMemberFormData.order}
                  onChange={(e) => setTeamMemberFormData({ ...teamMemberFormData, order: parseInt(e.target.value) || 0 })}
                  placeholder="Display order"
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingMember ? '💾 Update' : '➕ Add'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowTeamMemberModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {showAttendanceModal && selectedEventForAttendance && (
        <div className="modal-overlay attendance-modal-overlay" onClick={() => setShowAttendanceModal(false)}>
          <div className="modal-content attendance-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close attendance-modal-close" onClick={() => setShowAttendanceModal(false)}>
              ✕
            </button>
            <div className="attendance-modal-header">
              <div className="attendance-modal-icon">📝</div>
              <div className="attendance-modal-title-section">
                <h2 className="attendance-modal-title">Mark Attendance</h2>
                <h3 className="attendance-modal-event-title">{selectedEventForAttendance.title}</h3>
                <p className="attendance-modal-description">
                  Select who actually attended the event. Points ({selectedEventForAttendance.points || 10} per person) will be added to selected users.
                </p>
              </div>
            </div>
            <div className="attendance-modal-body">
              <div className="attendance-list">
                {selectedEventForAttendance.registrations
                  ?.filter(r => r.status === 'coming')
                  .map((reg) => (
                    <div key={reg.id} className="attendance-item">
                      <div className="attendance-user-info">
                        <div className="attendance-user-avatar">
                          {reg.user?.name ? reg.user.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div className="attendance-user-details">
                          <span className="attendance-user-name">{reg.user?.name || 'Unknown User'}</span>
                          {reg.user?.role && (
                            <span className="attendance-user-role">{reg.user.role}</span>
                          )}
                        </div>
                      </div>
                      <label className="attendance-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="attendance-checkbox-input"
                          checked={attendanceData[reg.user_id] || false}
                          onChange={() => handleToggleAttendance(reg.user_id)}
                        />
                        <span className="attendance-checkbox-label">
                          <span className="attendance-checkbox-custom"></span>
                          <span className="attendance-checkbox-text">Attended</span>
                        </span>
                      </label>
                    </div>
                  ))}
              </div>
            </div>
            <div className="attendance-modal-footer">
              <button
                className="btn-secondary attendance-cancel-btn"
                onClick={() => {
                  setShowAttendanceModal(false);
                  setSelectedEventForAttendance(null);
                  setAttendanceData({});
                }}
                disabled={savingAttendance}
              >
                Cancel
              </button>
              <button
                className="btn-primary attendance-save-btn"
                onClick={handleSaveAttendance}
                disabled={savingAttendance}
              >
                {savingAttendance ? (
                  <>
                    <span className="loading-spinner-small"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    💾 Save Attendance
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Discount Place Modal */}
      {showDiscountPlaceModal && (
        <div className="modal-overlay" onClick={() => setShowDiscountPlaceModal(false)}>
          <div className="modal-content create-event-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => {
              setShowDiscountPlaceModal(false);
              setEditingDiscountPlace(null);
              setDiscountPlaceFormData({ 
                place_name: '', 
                discount_percentage: '', 
                promo_code_suffix: '', 
                description: '', 
                user_id: '',
                logo: null 
              });
              setDiscountPlaceError('');
            }}>✕</button>
            <div className="modal-header">
              <h2>{editingDiscountPlace ? '✏️ Edit Discount Place' : '➕ Add Discount Place'}</h2>
            </div>
            
            {discountPlaceError && <div className="error-message">{discountPlaceError}</div>}
            
            <form onSubmit={handleCreateDiscountPlace}>
              <div className="form-group">
                <label>🏪 Place Name</label>
                <input
                  type="text"
                  name="place_name"
                  value={discountPlaceFormData.place_name}
                  onChange={handleDiscountPlaceFormChange}
                  placeholder="Enter place name"
                  required
                />
              </div>

              <div className="form-group">
                <label>💰 Discount Percentage (%)</label>
                <input
                  type="number"
                  name="discount_percentage"
                  value={discountPlaceFormData.discount_percentage}
                  onChange={handleDiscountPlaceFormChange}
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="Enter discount percentage"
                  required
                />
              </div>

              <div className="form-group">
                <label>🎟️ Promo Code Suffix</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ 
                    color: '#00ff88', 
                    fontSize: '16px', 
                    fontWeight: '700',
                    padding: '8px 12px',
                    background: 'rgba(0, 255, 136, 0.1)',
                    border: '1px solid rgba(0, 255, 136, 0.3)',
                    borderRadius: '6px'
                  }}>
                    ALIENS-
                  </span>
                  <input
                    type="text"
                    name="promo_code_suffix"
                    value={discountPlaceFormData.promo_code_suffix}
                    onChange={(e) => {
                      const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                      setDiscountPlaceFormData({ ...discountPlaceFormData, promo_code_suffix: cleaned });
                    }}
                    placeholder="E.g. REST"
                    maxLength="20"
                    required
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <small className="form-help">Final code: ALIENS-{discountPlaceFormData.promo_code_suffix || 'XXX'}</small>
              </div>

              {isFounder && (
                <div className="form-group">
                  <label>👤 Assign to Owner (Optional)</label>
                  <select
                    name="user_id"
                    value={discountPlaceFormData.user_id}
                    onChange={handleDiscountPlaceFormChange}
                    className="filter-select"
                  >
                    <option value="">Select an owner (defaults to you)</option>
                    {allUsers
                      .filter(u => u.role === 'sponsor' || u.role === 'place_owner')
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email}) - {user.role}
                        </option>
                      ))}
                  </select>
                  <small className="form-help">Select the user who will manage this discount place</small>
                </div>
              )}

              <div className="form-group">
                <label>📄 Description</label>
                <textarea
                  name="description"
                  value={discountPlaceFormData.description}
                  onChange={handleDiscountPlaceFormChange}
                  placeholder="Enter place description"
                  rows="5"
                />
              </div>

              <div className="form-group">
                <label>🖼️ Logo</label>
                <input
                  type="file"
                  name="logo"
                  accept="image/*"
                  onChange={handleDiscountPlaceFormChange}
                />
                {(editingDiscountPlace?.logo || discountPlaceFormData.logo) && (
                  <div className="preview-container">
                    <img 
                      src={discountPlaceFormData.logo ? URL.createObjectURL(discountPlaceFormData.logo) : (editingDiscountPlace.logo_url || `https://aliensteam.com/storage/app/public/${editingDiscountPlace.logo}`)} 
                      alt="Logo preview" 
                      className="poster-preview"
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={creatingDiscountPlace}>
                  {creatingDiscountPlace ? 'Saving...' : (editingDiscountPlace ? '💾 Update Place' : '➕ Create Place')}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowDiscountPlaceModal(false);
                    setEditingDiscountPlace(null);
                    setDiscountPlaceFormData({ 
                      place_name: '', 
                      discount_percentage: '', 
                      promo_code_suffix: '', 
                      description: '', 
                      user_id: '',
                      logo: null 
                    });
                    setDiscountPlaceError('');
                  }}
                  disabled={creatingDiscountPlace}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm || (() => {})}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default AdminDashboard;








